'use client';

import './consent-banner.scss';

import type { ReactNode } from 'react';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.tsx';
import { HtmlBlock } from '@sola/ark-core/atoms/html/html.tsx';
import type { ConsentBannerUIProps } from '@sola/tracking/lib/consent-banner-data.ts';

/**
 * Presentational consent banner component.
 * Renders a fixed-bottom banner with configurable text and action buttons.
 * All behavior (consent logic, cookie writes) is handled by the parent.
 */
export const ConsentBannerUI: React.FC<
  ConsentBannerUIProps & { children?: ReactNode }
> = ({
  title,
  description,
  acceptAllButton,
  rejectAllButton,
  showPreferencesButton,
  policyLink,
  children,
}) => {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Cookie consent'}
      className="ConsentBanner"
    >
      <div className="ConsentBanner__container container mx-auto">
        {title && <h2 className="ConsentBanner__title heading-sm">{title}</h2>}

        {description && (
          <HtmlBlock
            htmlString={description}
            tag="p"
            className="ConsentBanner__description label-md md:body-sm"
          />
        )}

        {children}

        <div className="ConsentBanner__actions">
          <ButtonStyle size="md">
            <button type="button" onClick={acceptAllButton.onClick}>
              {acceptAllButton.text}
            </button>
          </ButtonStyle>

          {rejectAllButton && (
            <ButtonStyle size="md" variant="secondary">
              <button type="button" onClick={rejectAllButton.onClick}>
                {rejectAllButton.text}
              </button>
            </ButtonStyle>
          )}

          {showPreferencesButton && (
            <ButtonStyle size="md" variant="tertiary">
              <button
                type="button"
                disabled={showPreferencesButton.disabled}
                onClick={showPreferencesButton.onClick}
              >
                {showPreferencesButton.text}
              </button>
            </ButtonStyle>
          )}

          {policyLink && (
            <a
              href={policyLink.url}
              target="_blank"
              rel="noopener noreferrer"
              className="ConsentBanner__policyLink label-sm"
            >
              {policyLink.text}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
